import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  ArrowRight, ShieldCheck, Lightning, Lock, CurrencyDollar, Sparkle,
  Storefront, Crown, ChatCircleText, Check
} from '@phosphor-icons/react';
import Navbar from '@/components/Navbar';

// ─────────────────────────────────────────────────────────────────────────────
// /join — founding-vendor recruitment page.
//
// This is where cold-call / outreach vendors land. It deliberately does NOT
// send them to the public Browse page (which is customer-facing and, pre-launch,
// intentionally empty). Instead it sells joining EARLY as the advantage:
// founding vendors get priority placement, first pick of demand, and a direct
// line to the founder. Everything quoted here is real: 6% flat fee, no
// subscription, escrow, 24h review. CTAs go to the provider signup flow.
// ─────────────────────────────────────────────────────────────────────────────

const CTA_TO = '/auth?mode=register&role=provider';

export default function JoinProviderPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // A provider already signed in should go straight to their setup/dashboard.
  const startApplying = () => {
    if (!user) navigate(CTA_TO);
    else if (user.role === 'provider') navigate('/provider/dashboard');
    else navigate('/provider/setup-profile');
  };

  const perks = [
    {
      icon: <Crown size={22} weight="fill" className="text-primary" />,
      title: 'Founding-vendor placement',
      desc: 'Join before launch and you sit at the front of the line — priority placement when the first wave of customers arrives, before the category fills up.',
    },
    {
      icon: <Sparkle size={22} weight="fill" className="text-primary" />,
      title: 'Our AI sends you clients',
      desc: 'Customers describe their event to our AI planner and it books real vendors who fit. Early vendors are the ones it has to choose from — that demand is yours to capture.',
    },
    {
      icon: <CurrencyDollar size={22} weight="fill" className="text-primary" />,
      title: '6% flat — no subscription',
      desc: 'No monthly fee, no listing fee, no lock-in. You only pay 6% when you actually earn. Keep your prices, keep your clients.',
    },
    {
      icon: <Lock size={22} weight="fill" className="text-primary" />,
      title: 'You get paid, protected',
      desc: 'Every booking is paid up front and held in escrow, then released to you after the job is done. No chasing invoices, no no-shows.',
    },
    {
      icon: <ChatCircleText size={22} weight="fill" className="text-primary" />,
      title: 'A direct line to the founder',
      desc: 'As a founding vendor you help shape the platform. Real feedback, real changes, real fast — you are not a ticket number.',
    },
    {
      icon: <ShieldCheck size={22} weight="fill" className="text-primary" />,
      title: 'A brand customers trust',
      desc: 'Verified vendors, secure payment, no off-platform risk. Being on Bookr signals to customers that you are a professional they can book with confidence.',
    },
  ];

  const steps = [
    { n: '1', title: 'Create your vendor account', desc: 'Takes a couple of minutes. Tell us your trade, your area, and your services.' },
    { n: '2', title: 'Build your profile', desc: 'Add photos, pricing, and availability. This is what customers and the AI planner see.' },
    { n: '3', title: 'Get approved in ~24h', desc: 'We review every vendor by hand to keep quality high. Fast turnaround.' },
    { n: '4', title: 'Start taking bookings', desc: 'Show up in Browse and in AI-planned events. Get paid securely, on time.' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.06] via-transparent to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 pt-16 pb-14 sm:pt-24 sm:pb-20 text-center relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3.5 py-1.5 mb-6">
            <Storefront size={15} weight="fill" className="text-primary" />
            <span className="text-xs font-semibold text-primary tracking-wide uppercase">Now onboarding founding vendors — Montreal</span>
          </div>
          <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-foreground leading-[1.05]">
            Get booked for the events<br className="hidden sm:block" />
            <span className="text-primary italic"> people are already planning.</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Bookr is an AI event concierge that plans people's events and books real
            vendors to make them happen. Be one of the first pros on it — and be who the AI reaches for.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={startApplying}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl px-7 h-13 py-3.5 text-base font-semibold btn-anim hover:opacity-95 cursor-pointer shadow-lift"
            >
              Become a founding vendor <ArrowRight size={18} weight="bold" />
            </button>
            <Link
              to="/browse"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-border bg-card text-foreground rounded-xl px-7 h-13 py-3.5 text-base font-medium btn-anim hover:bg-secondary/60 cursor-pointer"
            >
              See how customers book
            </Link>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">No monthly fee · 6% only when you earn · Free to join</p>
        </div>
      </section>

      {/* ── Stat band (all real terms) ── */}
      <section className="border-y border-border bg-secondary/30">
        <div className="max-w-5xl mx-auto px-5 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: '6%', label: 'Flat fee — no subscriptions' },
            { value: '$0', label: 'To join & list your services' },
            { value: '100%', label: 'Escrow — paid after the job' },
            { value: '~24h', label: 'Application review' },
          ].map((s) => (
            <div key={s.label}>
              <div className="text-3xl sm:text-4xl font-black text-primary">{s.value}</div>
              <div className="mt-1 text-xs sm:text-sm text-muted-foreground leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Why join early ── */}
      <section className="max-w-6xl mx-auto px-5 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-foreground">Why get in early</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            A new platform's first vendors have the least competition and the most upside.
            Here's what being a founding vendor on Bookr gets you.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {perks.map((p) => (
            <div key={p.title} className="rounded-2xl border border-border bg-card p-6 hover:shadow-lift transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4">{p.icon}</div>
              <h3 className="text-base font-semibold text-foreground mb-1.5">{p.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="border-t border-border bg-secondary/20">
        <div className="max-w-5xl mx-auto px-5 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-foreground">From sign-up to booked in 4 steps</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s) => (
              <div key={s.n} className="relative rounded-2xl border border-border bg-card p-6">
                <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold mb-4">{s.n}</div>
                <h3 className="text-base font-semibold text-foreground mb-1.5">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who it's for ── */}
      <section className="max-w-5xl mx-auto px-5 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-foreground">Who books on Bookr</h2>
          <p className="mt-3 text-muted-foreground">If you run events, your customers are here.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {['DJs & Entertainment', 'Caterers', 'Venues', 'Photographers & Video', 'Decor & Rentals', 'Event Staffing', 'Bartenders', 'Planners', 'Florists', 'Home Services'].map((c) => (
            <span key={c} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-foreground">
              <Check size={15} weight="bold" className="text-primary" /> {c}
            </span>
          ))}
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-5 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2 mb-5">
            <Lightning size={18} weight="fill" className="text-primary" />
            <span className="text-xs font-semibold text-primary uppercase tracking-widest">Founding vendor spots open now</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-foreground leading-tight">
            Ready to get booked?
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Free to join. No subscription. 6% only when you earn. It takes a few minutes to
            set up your profile — and you could be the first vendor customers see in your category.
          </p>
          <button
            onClick={startApplying}
            className="mt-8 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl px-8 h-13 py-3.5 text-base font-semibold btn-anim hover:opacity-95 cursor-pointer shadow-lift"
          >
            Start my vendor profile <ArrowRight size={18} weight="bold" />
          </button>
          <p className="mt-5 text-sm text-muted-foreground">
            Already have an account? <Link to="/auth" className="text-primary font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
